// ============================================================
// ACTION RESOLVERS - Feint, Knock-Down, Stun, Stand.
//
// Each takes pre-built OpposedSides (the caller decides character vs
// statblock footing), rolls in a FIXED order the E2E dice sequence
// depends on, and returns deltas - conditions to merge, wounds to
// subtract - rather than mutating anything. Narrative composition is
// the caller's job, from the returned pieces.
//
// Roll order (the seeded-dice contract):
//   Feint:      actor d100, defender d100.
//   Knock-Down: actor d100, defender d100, then 1d5 only on an actor
//               win by 2+ DoS.
//   Stun:       actor d100 (WS -20), then on success actor 1d10 and
//               defender 1d10.
// ============================================================
import { rollOpposedTest, type OpposedSide, type OpposedResult, type OpposedTestOptions } from '../opposedTests';
import type { UnnaturalDosMode } from '../unnaturalDos';
import { condType } from '../conditions';
import { rollDie, rollDiceExpression } from '../dice';
import type { TestResult } from '../characteristicTests';
import { rollCharacteristicTest, rollPlainTargetTest } from '../characteristicTests';

export interface FeintOutcome {
  result: OpposedResult;
  /** Conditions to merge onto the ACTOR (the feint advantage). */
  actorGains: string[];
}

/** Opposed WS; a win promises the next melee attack cannot be avoided. */
export function resolveFeint(actor: OpposedSide, defender: OpposedSide, opts: OpposedTestOptions = {}): FeintOutcome {
  const result = rollOpposedTest(actor, defender, opts);
  return { result, actorGains: result.winner === 'actor' ? ['Ft'] : [] };
}

export interface KnockDownInput {
  actor: OpposedSide;
  defender: OpposedSide;
  /** Actor Strength Bonus - feeds the 1d5-3+SB rider. */
  actorSB: number;
  /** Defender's body armour and Toughness Bonus - the rider's soak. */
  defenderBodyAP: number;
  defenderTB: number;
  /** The table's Unnatural DoS ruling, for both sides (v8.310.10). */
  unnaturalDosMode?: UnnaturalDosMode;
}

export interface KnockDownOutcome {
  result: OpposedResult;
  actorConds: string[];
  defenderConds: string[];
  /** Present only on an actor win by 2+ DoS. */
  damage?: { rolled: number; soaked: number; dealt: number };
}

/**
 * Opposed Strength to floor the target. A win puts them Prone; a win by
 * 2+ DoS also deals 1d5-3+SB with Armour Points counting DOUBLE (TB
 * soaks as normal) and a level of Fatigue - the fatigue lands even when
 * the armour eats all the damage. The target winning by 2+ floors the
 * ACTOR instead.
 */
export function resolveKnockDown(input: KnockDownInput): KnockDownOutcome {
  const result = rollOpposedTest(input.actor, input.defender, { unnaturalDosMode: input.unnaturalDosMode });
  if (result.winner === 'actor') {
    const defenderConds = ['P'];
    if (result.margin >= 2) {
      const rolled = Math.max(0, rollDie(5) - 3 + input.actorSB);
      const soaked = Math.min(rolled, input.defenderBodyAP * 2 + input.defenderTB);
      defenderConds.push('F1');
      return { result, actorConds: [], defenderConds, damage: { rolled, soaked, dealt: rolled - soaked } };
    }
    return { result, actorConds: [], defenderConds };
  }
  if (result.winner === 'defender' && result.margin >= 2) {
    return { result, actorConds: ['P'], defenderConds: [] };
  }
  return { result, actorConds: [], defenderConds: [] };
}

export interface StunInput {
  /** The actor's WS footing; the -20 is applied here, not by the caller. */
  actor: OpposedSide;
  actorSB: number;
  defenderTB: number;
  defenderHeadAP: number;
  /** Unarmed, or a Primitive weapon: head armour counts double. */
  apDoubled: boolean;
  /** The table's Unnatural DoS ruling for the WS test (v8.310.10). */
  unnaturalDosMode?: UnnaturalDosMode;
}

export interface StunOutcome {
  test: TestResult;
  /** The 1d10 contest, present only when the WS test succeeded. */
  contest?: { actorTotal: number; defenderTotal: number; diff: number };
  /** Conditions to merge onto the DEFENDER (S rounds + fatigue). */
  defenderConds: string[];
}

/**
 * Hard (-20) WS to strike to Stun; on success 1d10+SB against
 * 1d10+TB+head AP (doubled for fists and Primitive weapons). Beat them
 * and the difference is rounds Stunned, plus a level of Fatigue.
 */
export function resolveStun(input: StunInput): StunOutcome {
  const side = input.actor;
  const mod = (side.mod ?? 0) - 20;
  const unnatural =
    input.unnaturalDosMode === undefined ? side.unnatural : { mode: input.unnaturalDosMode, ...(side.unnatural ?? {}) };
  const test =
    side.kind === 'characteristic'
      ? rollCharacteristicTest(side.character, side.charId, 0, mod, unnatural)
      : side.kind === 'plain'
        ? rollPlainTargetTest(side.target, side.testId, 0, mod, unnatural)
        : (() => {
            throw new Error('Stun opposes characteristics, not skills');
          })();
  if (!test.success) return { test, defenderConds: [] };

  const actorTotal = rollDiceExpression('1d10').total + input.actorSB;
  const defenderTotal =
    rollDiceExpression('1d10').total + input.defenderTB + input.defenderHeadAP * (input.apDoubled ? 2 : 1);
  const diff = actorTotal - defenderTotal;
  const contest = { actorTotal, defenderTotal, diff };
  if (diff <= 0) return { test, contest, defenderConds: [] };
  return { test, contest, defenderConds: [`S${diff}`, 'F1'] };
}

// ============================================================
// STAND - the one action here that rolls nothing.
// ============================================================

export interface StandOutcome {
  /** The actor's conditions with Prone removed. A NEW array. */
  conds: string[];
  /** False when they were already on their feet - the caller says so. */
  wasProne: boolean;
  /** Advisory: Leap Up makes standing free. */
  cost: 'free' | 'half';
  /** One line for the log, cost included. */
  summary: string;
}

/**
 * Get up. No test, no opposition - RAW is simply that standing from
 * Prone costs a Half Action, or nothing at all with Leap Up.
 *
 * Returns the new condition array rather than mutating, like every
 * resolver here, and reports `wasProne` so a caller pressing it on a
 * standing combatant can say "already on your feet" instead of logging
 * a triumph over nothing.
 */
export function resolveStand(conds: string[], opts: { hasLeapUp?: boolean } = {}): StandOutcome {
  const wasProne = conds.some((c) => condType(c) === 'P');
  const cost = opts.hasLeapUp ? 'free' : 'half';
  const costLabel = opts.hasLeapUp ? 'Free Action, Leap Up' : 'Half Action';
  return {
    conds: conds.filter((c) => condType(c) !== 'P'),
    wasProne,
    cost,
    summary: wasProne ? `Stands up (${costLabel}).` : 'Already on their feet - nothing to stand up from.',
  };
}
